import { useQuery } from '@tanstack/react-query';
import { dashboard, isForbidden } from '@cwos/api-client';
import { usePermissions } from '../../../permissions.js';
import type { dashboard as DashboardNS } from '@cwos/api-client';
import { Pill, Refused, SkeletonSet, type Column } from '@cwos/ui-admin';
import { useAnalyticsControls } from '../AnalyticsContext.js';
import { periodRangeLabel, type AnalyticsPreset } from '../analyticsRange.js';
import { gapRatioLabel, gapSeverityLabel, gapSeverityTone } from '../../dashboardLogic.js';
import { WeeklyPanel, TrendFailure } from '../drilldown.js';
import { METRIC_ABOUT, NO_TARGETS_SENTENCE } from '../explain.js';
import {
  DisclosureNote,
  DrilldownTable,
  Panel,
  Stat,
  StatGrid,
  StalenessStrip,
  type QueryLike,
} from '../shared.js';
import { LoadFailure } from '../../../LoadFailure.js';

type ClientCareGap = DashboardNS.ClientCareGap;

/**
 * F350 — WHICH LOOKBACK EACH WORKSPACE PERIOD MAPS TO, WRITTEN OUT IN FULL.
 *
 * This was `preset === 'this_week' || preset === 'last_week' ? '7d' : '30d'`,
 * which is not a mapping so much as two presets and a shrug. Every other period
 * — including `ytd`, `all` and a `custom` range the reader chose the dates for —
 * fell into `30d` without anything saying so, and a preset added to the
 * vocabulary tomorrow would have joined them in silence.
 *
 * A total `Record` keyed by `AnalyticsPreset` makes that impossible: a new
 * preset is a COMPILE error here, at the line that has to decide, which is the
 * same guard `resolveAnalyticsPeriod` now carries on the server.
 *
 * IT DOES NOT MAKE THE SUBSTITUTION GO AWAY, and it is not pretending to. The
 * care-gaps endpoint answers over `7d` or `30d` and nothing else, so "year to
 * date" genuinely has no window of its own to be given. What changes is that the
 * choice is now visible, reviewed, and — see `lookbackNote` below — SAID to the
 * reader, instead of being a default nobody wrote down.
 */
const CARE_GAP_LOOKBACK: Record<AnalyticsPreset, '7d' | '30d'> = {
  this_week: '7d',
  last_week: '7d',
  this_month: '30d',
  last_month: '30d',
  last_30_days: '30d',
  ytd: '30d',
  all: '30d',
  custom: '30d',
};

const CARE_GAP_LOOKBACK_LABEL: Record<'7d' | '30d', string> = {
  '7d': 'the last 7 days',
  '30d': 'the last 30 days',
};

export function CareGapsView() {
  const { preset, filterQuery, apiQuery } = useAnalyticsControls();
  const period = CARE_GAP_LOOKBACK[preset];

  const summary = useQuery({
    queryKey: ['dashboard', 'care-gaps', period, filterQuery],
    queryFn: () => dashboard.careGaps({ period, ...filterQuery }),
  });

  /**
   * DO NOT ASK A QUESTION WHOSE REFUSAL IS ALREADY KNOWN (F159).
   *
   * `/care-gaps/clients` is in the API's `ROW_LEVEL_ROUTES` — `dashboard:read`
   * only — while this view's summary is aggregate-readable, so an
   * `analytics:read`-only reader landed here on every visit and fired a probe
   * the server can only 403 (twice, before F160 stopped the retry). Unlike
   * `DocumentationView` there is no refused-summary outcome to gate on: the
   * summary SUCCEEDS for this reader. So the gate is the same client key table
   * `AnalyticsShell` hides the documentation tab with (F148's approved shape),
   * and it decides only whether to ASK — whenever a request is made, the
   * server's answer stays the authority, and the flattened key list erring
   * toward `true` (a program-scoped grant the server would refuse) costs one
   * refused request rendered by the `isForbidden` branch, exactly as before.
   * ONE variable drives both the query and the table's `withheld` prop, so the
   * two cannot disagree.
   */
  const { has } = usePermissions();
  const rowLevel = has('dashboard:read');

  const details = useQuery({
    queryKey: ['dashboard', 'care-gaps', 'clients', period, filterQuery],
    queryFn: () => dashboard.careGapDetails({ period, ...filterQuery }),
    enabled: rowLevel,
  });

  const trend = useQuery({
    queryKey: ['analytics', 'care-gaps', 'trend', apiQuery],
    queryFn: () => dashboard.analyticsCareGapsTrend(apiQuery),
  });

  // A 403 is an answer. It was taking the failure branch and getting a retry
  // button that cannot change it.
  if (summary.isError && isForbidden(summary.error)) {
    return (
      <Refused
        heading="This view is not shown to you"
        message="Your account does not have access to the figures behind it. Nothing has been left out of them — they are not yours to see."
      />
    );
  }
  if (summary.isLoading) return <SkeletonSet surface="table" rows={3} />;
  if (summary.isError && !summary.data) {
    return (
      <LoadFailure
        error={summary.error}
        message="We couldn’t load care gaps just now, so read this screen as unknown rather than empty."
        onRetry={() => void summary.refetch()}
      />
    );
  }
  const d = summary.data;
  // `return null` rendered a BLANK VIEW — the reader saw a page with nothing on
  // it and no reason given. A query that has not answered is pending.
  if (!d) return <SkeletonSet surface="table" rows={3} />;

  const columns: Column<ClientCareGap>[] = [
    {
      key: 'client',
      header: 'Client',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.clientLabel}</span>
          {r.serviceTypeName ? <span className="rn-muted"> · {r.serviceTypeName}</span> : null}
        </span>
      ),
    },
    {
      key: 'severity',
      header: 'Gap',
      render: (r) => (
        <Pill variant={gapSeverityTone(r.severity)} dot>
          {gapSeverityLabel(r.severity)}
        </Pill>
      ),
    },
    { key: 'delivered', header: 'Delivered', render: (r) => String(r.actualUnits), numeric: true },
    { key: 'target', header: 'Target', render: (r) => String(r.targetUnits), numeric: true },
    { key: 'ratio', header: 'Of plan', render: (r) => gapRatioLabel(r.gapRatio), numeric: true },
  ];

  return (
    <div className="an-stack">
      {/*
        F350 — SAY WHICH WINDOW THESE FIGURES ACTUALLY COVER.
        The period control at the top of the workspace can read "Year to date"
        while every number below it is a 30-day lookback, because that is the
        widest window this measure is published over. Before this line the reader
        had no way to know, which made a correct figure into a mislabelled one.
      */}
      <p className="rn-muted">
        Care gaps are measured over {CARE_GAP_LOOKBACK_LABEL[period]}. The workspace period chooses
        between a 7- and a 30-day window; it is not applied as a date range.
      </p>

      <StatGrid>
        <Stat label="With a plan" value={String(d.totalClientsWithTargets)} />
        <Stat label="With gaps" value={String(d.clientsWithGaps)} />
        <Stat label="Severe" value={String(d.gapCountBySeverity.severe)} />
        <Stat label="Moderate" value={String(d.gapCountBySeverity.moderate)} />
        <Stat label="Minor" value={String(d.gapCountBySeverity.minor)} />
      </StatGrid>

      <StalenessStrip staleness={d.staleness} />

      <CareGapsTrendPanel trend={trend} />

      <Panel title="Clients with gaps" subtitle="ordered by severity">
        <DrilldownTable
          withheld={!rowLevel}
          rows={details.data}
          isLoading={details.isLoading}
          isError={details.isError}
          error={details.error}
          onRetry={() => void details.refetch()}
          columns={columns}
          rowKey={(r) => r.clientId}
          emptyHeading="No care gaps"
          emptyMessage="Every client with a plan is on track right now."
        />
      </Panel>
    </div>
  );
}

function CareGapsTrendPanel({ trend }: { trend: QueryLike<DashboardNS.CareGapsTrend> }) {
  if (trend.isLoading) return <SkeletonSet surface="band" />;
  if (trend.isError && !trend.data) {
    return (
      <TrendFailure
        error={trend.error}
        message="We couldn’t load the care-gap trend just now."
        onRetry={() => void trend.refetch()}
      />
    );
  }
  const t = trend.data;
  if (!t) return null;
  const title = 'Clients below weekly target';
  // NO TARGET ON FILE IS NOT "NO GAPS". Two flat zero lines said every client
  // was on plan when no client had a plan to be measured against.
  const noTargets =
    t.clientsWithTargets.points.length > 0 &&
    t.clientsWithTargets.points.every((p) => p.kind === 'value' && p.value === 0);
  if (noTargets) {
    return (
      <Panel title={title} subtitle={`weekly · ${periodRangeLabel(t.period)}`}>
        <DisclosureNote heading="No targets to measure against" text={NO_TARGETS_SENTENCE} />
      </Panel>
    );
  }
  return (
    <WeeklyPanel
      title={title}
      axis={t}
      about={METRIC_ABOUT.careGaps}
      lines={[
        { name: 'Below target', color: 'var(--color-look)', series: t.gappedClients },
        {
          name: 'With a target',
          color: 'var(--chart-context)',
          dashed: true,
          series: t.clientsWithTargets,
        },
      ]}
    />
  );
}
